import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

function SectionCard({ title, value, detail, to, testId, loading = false }) {
  const t = useT();
  const content = (
    <>
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{title}</div>
      <div className="mt-2 break-words text-lg font-bold text-[var(--text)]">{value}</div>
      {detail ? <div className="mt-1 break-words text-xs leading-relaxed text-[var(--text-2)]">{detail}</div> : null}
    </>
  );
  const className = "block min-w-0 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] p-4 transition-colors";
  return to ? (
    <Link to={to} className={`${className} hover:border-[var(--accent)] hover:bg-[var(--surface-hover)]`} data-testid={testId} aria-busy={loading}>
      {content}
      <span className="mt-3 inline-block text-xs font-semibold text-[var(--accent-text)]">{t("pd.summary.open")} →</span>
    </Link>
  ) : (
    <div className={className} data-testid={testId} aria-busy={loading}>{content}</div>
  );
}

export default function ProjectOverview({ project, device, version, versionLoading = false, versionError = false, role, readinessItems = [] }) {
  const t = useT();
  const [postmarket, setPostmarket] = useState({ loading: true, failed: false, pms: 0, trace: 0, complaints: 0, openComplaints: 0 });
  const projectId = project?.id;
  const deviceId = device?.id;
  const classification = version?.classification || {};
  const mdrClass = classification.suggested_class || device?.mdr_class || "";
  const reviewCount = Array.isArray(classification.review_items) ? classification.review_items.length : 0;
  const reviewedCount = readinessItems.filter((item) => item.status === "recorded").length;
  const pendingCount = Math.max(0, readinessItems.length - reviewedCount);
  useEffect(() => {
    if (!projectId || !deviceId) {
      setPostmarket((current) => ({ ...current, loading: false, failed: true }));
      return undefined;
    }
    let cancelled = false;
    const deviceParams = { params: { device_id: deviceId } };
    setPostmarket({ loading: true, failed: false, pms: 0, trace: 0, complaints: 0, openComplaints: 0 });
    Promise.allSettled([
      api.get(`/projects/${projectId}/pms`, deviceParams),
      api.get(`/projects/${projectId}/trace`, deviceParams),
      api.get(`/projects/${projectId}/qms/complaint`, deviceParams),
    ]).then((results) => {
      if (cancelled) return;
      const [pms, trace, complaints] = results.map((result) => (
        result.status === "fulfilled" && Array.isArray(result.value.data) ? result.value.data : null
      ));
      setPostmarket({
        loading: false,
        failed: results.some((result) => result.status !== "fulfilled" || !Array.isArray(result.value?.data)),
        pms: pms?.length ?? null,
        trace: trace?.length ?? null,
        complaints: complaints?.length ?? null,
        openComplaints: complaints
          ? complaints.filter((item) => !["closed", "resolved"].includes(String(item.status || "").toLowerCase())).length
          : null,
      });
    });
    return () => { cancelled = true; };
  }, [projectId, deviceId]);

  const productQuery = deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : "";
  const sectionUrl = (section) => `/projects/${encodeURIComponent(projectId)}?section=${section}${productQuery}`;
  const classificationStatus = versionLoading
    ? t("workspace.loading")
    : versionError
      ? t("pd.summary.unavailable")
      : !version
    ? t("pd.summary.no_assessment")
    : mdrClass
      ? (classification.needs_human_review || reviewCount > 0
        ? t("pd.summary.review_needed").replace("{count}", String(reviewCount || 1))
        : t("pd.summary.class_ready").replace("{class}", mdrClass))
      : t("pd.summary.class_pending");
  const regulatoryStatus = device?.regulatory_status ? t(`dev.st.${device.regulatory_status}`) : t("pd.summary.not_recorded");
  const eudamedStatus = device?.eudamed_registration_status ? t(`dev.eud.${device.eudamed_registration_status}`) : t("pd.summary.not_recorded");
  const pmsValue = postmarket.loading
    ? t("workspace.loading")
    : postmarket.pms === null ? t("pd.summary.unavailable") : String(postmarket.pms);
  const postmarketDetail = postmarket.loading
    ? t("pd.summary.postmarket_loading")
    : postmarket.failed
      ? t("pd.summary.postmarket_partial")
      : t("pd.summary.postmarket_counts")
        .replace("{trace}", String(postmarket.trace))
        .replace("{complaints}", String(postmarket.complaints))
        .replace("{open}", String(postmarket.openComplaints));

  return (
    <section className="space-y-4" data-testid="project-overview">
      <div>
        <p className="text-xs leading-relaxed text-[var(--text-2)]">{t("pd.summary.scope")}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SectionCard
          title={t("pd.summary.classification")}
          value={classificationStatus}
          detail={mdrClass ? t("pd.summary.preliminary_class").replace("{class}", mdrClass) : ""}
          to={role === "manufacturer" ? sectionUrl("classification") : undefined}
          testId="overview-classification"
          loading={versionLoading}
        />
        <SectionCard
          title={t("pd.summary.documents")}
          value={t("pd.summary.docs_count").replace("{ready}", String(reviewedCount)).replace("{total}", String(readinessItems.length))}
          detail={t("pd.summary.docs_pending").replace("{count}", String(pendingCount))}
          to={sectionUrl("documents")}
          testId="overview-documents"
        />
        <SectionCard
          title={t("pd.summary.market_eudamed")}
          value={`${t("pd.summary.market")}: ${regulatoryStatus}`}
          detail={`${t("pd.summary.eudamed")}: ${eudamedStatus}${device?.first_placed_on_market ? ` · ${t("pd.summary.first_market_date")}: ${device.first_placed_on_market}` : ""}${device?.eudamed_di_number ? ` · DI: ${device.eudamed_di_number}` : ""}`}
          testId="overview-market-eudamed"
        />
        <SectionCard
          title={t("pd.summary.postmarket")}
          value={`${t("pd.summary.pms_reports")}: ${pmsValue}`}
          detail={postmarketDetail}
          to={sectionUrl("traceability")}
          testId="overview-postmarket"
          loading={postmarket.loading}
        />
      </div>
      <p className="text-[11px] leading-relaxed text-[var(--text-2)]" data-testid="overview-status-disclaimer">
        {t("pd.summary.status_disclaimer")}
      </p>
    </section>
  );
}
